"use client"

import { useState, useEffect } from "react"
import { ChevronLeft, ChevronRight } from "lucide-react"

export function IndustriesCarousel() {
  const [currentSlide, setCurrentSlide] = useState(0)
  const [isAutoPlaying, setIsAutoPlaying] = useState(true)

  const industries = [
    {
      id: 1,
      title: "Healthcare",
      image: "https://images.unsplash.com/photo-1579684385127-1ef15d508118?w=800&q=80",
      description: "Digital solutions for healthcare providers",
    },
    {
      id: 2,
      title: "E-commerce",
      image: "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=800&q=80",
      description: "Online retail and marketplace solutions",
    },
    {
      id: 3,
      title: "Creative Arts",
      image: "https://images.unsplash.com/photo-1513364776144-60967b0f800f?w=800&q=80",
      description: "Portfolio and gallery management systems",
    },
    {
      id: 4,
      title: "Education",
      image: "https://images.unsplash.com/photo-1523240795612-9a054b0db644?w=800&q=80",
      description: "Learning management and educational platforms",
    },
    {
      id: 5,
      title: "Finance",
      image: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=800&q=80",
      description: "Financial technology and banking solutions",
    },
  ]

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % industries.length)
  }

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + industries.length) % industries.length)
  }

  const goToSlide = (index) => {
    setCurrentSlide(index)
  }

  useEffect(() => {
    if (!isAutoPlaying) return
    const interval = setInterval(nextSlide, 4000)
    return () => clearInterval(interval)
  }, [isAutoPlaying])

  const getVisibleSlides = () => {
    const slides = []
    for (let i = 0; i < 3; i++) {
      const index = (currentSlide + i) % industries.length
      slides.push(industries[index])
    }
    return slides
  }

  return (
    <section className="py-10 sm:py-16 px-4 bg-rose-50">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-8 sm:mb-12">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 sm:mb-4 px-2">
            Industries We <span className="text-blue-600">Serve</span>
          </h2>
          <p className="text-gray-600 text-base sm:text-lg px-2">Delivering tailored solutions across diverse sectors</p>
        </div>

        <div
          className="relative"
          onMouseEnter={() => setIsAutoPlaying(false)}
          onMouseLeave={() => setIsAutoPlaying(true)}
        >
          {/* Desktop View */}
          <div className="hidden md:grid md:grid-cols-3 gap-6 mb-8">
            {getVisibleSlides().map((industry, index) => (
              <div
                key={industry.id}
                className={`group cursor-pointer transition-all duration-500 ${index === 1 ? "scale-105 z-10" : "scale-95 opacity-75"
                  }`}
                onClick={() => goToSlide((currentSlide + index) % industries.length)}
              >
                <div className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300">
                  <div className="relative overflow-hidden">
                    <img
                      src={industry.image}
                      alt={industry.title}
                      className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    <div className="absolute bottom-4 left-4 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <h3 className="text-xl font-bold">{industry.title}</h3>
                      <p className="text-sm">{industry.description}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Mobile View */}
          <div className="md:hidden mb-8">
            <div className="bg-white rounded-2xl overflow-hidden shadow-lg">
              <div className="relative overflow-hidden">
                <img
                  src={industries[currentSlide].image}
                  alt={industries[currentSlide].title}
                  className="w-full h-64 object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                <div className="absolute bottom-4 left-4 text-white">
                  <h3 className="text-xl font-bold">{industries[currentSlide].title}</h3>
                  <p className="text-sm">{industries[currentSlide].description}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Navigation Buttons */}
          <div className="flex justify-center items-center gap-4">
            <button
              onClick={prevSlide}
              className="p-2 bg-white border border-blue-200 rounded-full hover:bg-blue-50 transition-all"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>

            {/* Dots */}
            <div className="flex gap-2">
              {industries.map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToSlide(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${index === currentSlide ? "bg-blue-600 scale-125" : "bg-blue-200 hover:bg-blue-300"
                    }`}
                />
              ))}
            </div>

            <button
              onClick={nextSlide}
              className="p-2 bg-white border border-blue-200 rounded-full hover:bg-blue-50 transition-all"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </section>
  )
}
